/**
 * Guided-tour engine provider (EPIC #1321, part 1/9).
 *
 * Mounts react-joyride ONCE at app root in controlled mode (we own
 * ``stepIndex``) so a single tour can navigate across react-router routes
 * between steps. Responsibilities:
 *   - filter a tour's steps by permission flag + module exactly like the
 *     sidebar's ``itemVisible`` (REQ D4-ONB-004) so a tour never points at a
 *     surface the user cannot open and an all-filtered tour never starts;
 *   - drive Back / Next / Skip-section / End / cancel transitions and report
 *     the outcome to the (stubbed-until-2/9) progress callback (D4-ONB-003);
 *   - orchestrate the nav: expose the accordion group to expand + whether the
 *     mobile drawer should open, which CrmSidebar / CrmLayout read via useTour;
 *   - render a fully custom, brand-token tooltip (D4-ONB-002) — no library UI;
 *   - render agency-neutral copy from BrandingContext (D4-ONB-011 / D4-WLB).
 *
 * The hook + context object live in ./tour so this module exports only the
 * provider component (Fast Refresh / lint).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Joyride, ACTIONS, EVENTS, STATUS } from "react-joyride";
import type { EventData, Step } from "react-joyride";
import { useAuth } from "./auth";
import { useModules } from "./modules";
import { useBranding } from "../hooks/useBranding";
import { HINT_ID_PREFIX, TourContext, isHintTourId } from "./tour";
import type {
  HintResult,
  ShowHintOptions,
  StartTourOptions,
  TourContextValue,
  TourHint,
} from "./tour";
import type { TourGateContext, TourStep } from "../lib/tours/types";
import { getTour } from "../lib/tours/registry";
import {
  isTourAvailable,
  nextSectionIndex,
  visibleSteps,
} from "../lib/tours/filter";
import { interpolateAgency } from "../lib/tours/interpolate";
import { routeMatchesStep, stepRouteAction } from "../lib/tours/routeMatch";
import {
  TOUR_FLOATING_OPTIONS,
  scrollHintTargetIntoView,
  stickyHeaderOffset,
  toJoyrideStep,
} from "../lib/tours/joyride";
import { recordTourProgress, recordTourStarted } from "../lib/tours/progress";
import type { TourOutcome } from "../lib/tours/progress";
import { TourTooltip } from "../components/tours/TourTooltip";
import { LAYOUT_POLL_MS, layoutKey, waitForLayoutToSettle } from "../lib/tours/layoutShift";
import { anchorSelector, isAnchorId } from "../lib/tours/anchorSelector";

/** How long the ``openVia`` driver waits for a not-yet-mounted opener (ms) --
 * the same budget react-joyride gets for the step's target. */
const OPENER_WAIT_MS = 8000;
const OPENER_POLL_MS = 150;

/** How long ``showHint`` looks for its control after opening the page (ms) --
 * long enough for a lazy page to render, far short of the tours' 8s wait. */
const HINT_WAIT_MS = 2500;
const HINT_POLL_MS = 100;

/**
 * The hint's control, if it is on the page and actually rendered (not hidden).
 * A page with a phone layout and a desktop layout renders the same anchor twice
 * and hides one copy, and a list repeats a row's anchor, so the first copy that
 * is actually on screen wins, not the first in the document (#3633).
 */
function findHintTarget(target: string): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>(anchorSelector(target));
  for (const el of Array.from(all)) {
    if (el.getClientRects().length > 0) return el;
  }
  return null;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const branding = useBranding();
  const { user, loading: authLoading } = useAuth();
  const { isEnabled, loading: modulesLoading } = useModules();

  // Live permission/module gate — same shape CrmSidebar.itemVisible uses.
  const gate: TourGateContext = useMemo(
    () => ({
      has: (flag) => Boolean(user?.permission_flags?.[flag]),
      isModuleEnabled: (module) => isEnabled(module),
      // Whole-tour audience gate inputs (issue #1347).
      isAdmin: user?.role === "admin",
      preset: user?.permission_preset ?? null,
    }),
    [user, isEnabled],
  );

  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);

  // Refs so the (stable) callbacks read current values without re-binding.
  const runRef = useRef(run);
  const stepsRef = useRef(steps);
  const stepIndexRef = useRef(stepIndex);
  const tourIdRef = useRef(activeTourId);
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  // D10-ACT-004 (#3606): the open hint's close callback, and a generation
  // counter so a newer hint or tour supersedes one still looking for its target.
  const hintEndRef = useRef<(() => void) | null>(null);
  const hintGenRef = useRef(0);
  // #3624: the running tour's own end callback (``startTour`` options).
  const tourEndRef = useRef<(() => void) | null>(null);
  runRef.current = run;
  stepsRef.current = steps;
  stepIndexRef.current = stepIndex;
  tourIdRef.current = activeTourId;
  // Tracks which step (id) we have already driven an ``openVia`` click for, so a
  // re-render never re-toggles the modal (issue #1342).
  const openedForStep = useRef<string | null>(null);
  // Records the id of the step that drove a modal open via ``openVia`` while that
  // modal is open, so the cleanup effect can close it once the tour leaves that
  // step (issue #1342). Null when no tour-opened modal is showing.
  const modalStepOpen = useRef<string | null>(null);
  // True while Back is returning the user from a record page to a HAND-OFF
  // step's list route (D9 WP24, #3551) -- suppresses the hand-off's advance.
  const returningToHandoff = useRef(false);

  const currentStep = steps[stepIndex] ?? null;
  const onRoute = currentStep
    ? routeMatchesStep(location.pathname, currentStep.route)
    : false;
  // Mirror onRoute into a ref so the stable event handler reads the live value
  // without re-binding (issue #1342).
  const onRouteRef = useRef(onRoute);
  onRouteRef.current = onRoute;

  const finish = useCallback((outcome: TourOutcome) => {
    if (!runRef.current) return;
    const hint = isHintTourId(tourIdRef.current);
    // A hint is not a tour: it never writes TourProgress (D10-ACT-004).
    if (!hint) {
      recordTourProgress({
        tourId: tourIdRef.current ?? "",
        outcome,
        lastStepIndex: stepIndexRef.current,
        totalSteps: stepsRef.current.length,
      });
    }
    const onHintEnd = hint ? hintEndRef.current : null;
    hintEndRef.current = null;
    const onTourEnd = hint ? null : tourEndRef.current;
    tourEndRef.current = null;
    // Refs too, so a caller acting in onHintEnd sees the engine idle at once.
    runRef.current = false;
    tourIdRef.current = null;
    openedForStep.current = null;
    returningToHandoff.current = false;
    setRun(false);
    setActiveTourId(null);
    setSteps([]);
    setStepIndex(0);
    onHintEnd?.();
    onTourEnd?.();
  }, []);

  const startTour = useCallback(
    (tourId: string, options: StartTourOptions = {}) => {
      const tour = getTour(tourId);
      if (!tour) return;
      const visible = visibleSteps(tour, gate);
      if (visible.length === 0) return; // all steps filtered — never offer it
      // A real tour replaces any hint (showing or still looking for its target).
      hintGenRef.current += 1;
      hintEndRef.current = null;
      tourEndRef.current = options.onEnd ?? null;
      setActiveTourId(tourId);
      setSteps(visible);
      setStepIndex(0);
      setRun(true);
      // Persist the start immediately so a tour opened but never finished is
      // already recorded as resumable (2/9 + 3/9). Fire-and-forget.
      recordTourStarted(tourId);
    },
    [gate],
  );

  const next = useCallback(() => {
    const last = stepsRef.current.length - 1;
    if (stepIndexRef.current >= last) {
      finish("completed");
      return;
    }
    setStepIndex((i) => Math.min(i + 1, last));
  }, [finish]);

  const back = useCallback(() => {
    const target = Math.max(0, stepIndexRef.current - 1);
    // D9 WP24 (#3551): stepping Back onto a HAND-OFF step from the record page
    // it handed off to must take the user back to the list -- NOT be read as
    // "the user opened a record" and advance straight back again. The nav
    // effect honours this flag until the list route is reached.
    if (stepsRef.current[target]?.advanceOnRoute) {
      returningToHandoff.current = true;
    }
    setStepIndex(target);
  }, []);

  const skipSection = useCallback(() => {
    const idx = nextSectionIndex(stepsRef.current, stepIndexRef.current);
    if (idx === -1) {
      finish("skipped");
      return;
    }
    setStepIndex(idx);
  }, [finish]);

  const endTour = useCallback(
    (outcome: "skipped" | "cancelled" = "cancelled") => finish(outcome),
    [finish],
  );

  // One-off hint (D10-ACT-004, #3606). Reuses the tour renderer with a single
  // synthetic step, but: never records progress (finish/startTour bypass),
  // refuses to interrupt a running tour, and checks the anchor is actually on
  // the page before showing anything -- a missing anchor is a quiet no-op
  // rather than the tours' 8s TARGET_NOT_FOUND wait.
  const showHint = useCallback(
    async (hint: TourHint, options: ShowHintOptions = {}): Promise<HintResult> => {
      if (runRef.current) return "busy";
      if (!isAnchorId(hint.target) || !hint.route.startsWith("/")) {
        return "missing";
      }
      const gen = ++hintGenRef.current;
      if (!routeMatchesStep(pathnameRef.current, hint.route)) navigate(hint.route);
      const deadline = Date.now() + (options.waitMs ?? HINT_WAIT_MS);
      const found = await new Promise<boolean>((resolve) => {
        const check = () => {
          if (hintGenRef.current !== gen || runRef.current) return resolve(false);
          if (routeMatchesStep(pathnameRef.current, hint.route) && findHintTarget(hint.target)) {
            return resolve(true);
          }
          if (Date.now() >= deadline) return resolve(false);
          window.setTimeout(check, HINT_POLL_MS);
        };
        check();
      });
      if (hintGenRef.current !== gen || runRef.current) return "busy";
      if (!found) return "missing";
      // Let late content above the control land first, so the tooltip opens
      // where the control will stay (#3624, D4).
      await waitForLayoutToSettle(
        () => findHintTarget(hint.target),
        () => hintGenRef.current === gen && !runRef.current,
      );
      if (hintGenRef.current !== gen || runRef.current) return "busy";
      const targetEl = findHintTarget(hint.target);
      if (!targetEl) return "missing";
      // The tour engine never scrolls a one-step hint, so the control is
      // brought into view here: sideways inside a tab strip on a phone, and
      // below the sticky header when it is above the fold (#3633).
      scrollHintTargetIntoView(targetEl);
      const step: TourStep = {
        id: "hint",
        route: hint.route,
        target: hint.target,
        title: hint.title,
        body: hint.body,
        kind: "page",
      };
      hintEndRef.current = options.onEnd ?? null;
      const id = `${HINT_ID_PREFIX}${hint.target}`;
      runRef.current = true;
      tourIdRef.current = id;
      setActiveTourId(id);
      setSteps([step]);
      setStepIndex(0);
      setRun(true);
      return "shown";
    },
    [navigate],
  );

  // Cross-route navigation: move to the step's route before highlighting.
  useEffect(() => {
    if (!run || !currentStep) return;
    // ``:param``-aware (issue #1956): when the step route is a ``/:id`` pattern
    // and we are ALREADY on a matching concrete path (e.g. ``/legal/1822`` vs
    // ``/legal/:otpId``), DO NOT navigate -- navigating to the literal pattern
    // would render ``:otpId`` and fetch a NaN record (404). A static (``:``-free)
    // route keeps exact behaviour.
    const action = stepRouteAction(location.pathname, currentStep);
    if (action === "stay") {
      returningToHandoff.current = false;
      return;
    }
    // A hint belongs to the page it points at: when the user goes elsewhere,
    // close it rather than pulling them back (D10-ACT-004).
    if (isHintTourId(activeTourId)) {
      finish("cancelled");
      return;
    }
    // D9 WP24 (#3551): a HAND-OFF step (``advanceOnRoute``) waits for the user
    // to open a record themselves -- the moment they land on a matching record
    // page, move on to the next step (which lives on that pattern route)
    // instead of pulling them back to the list. Arriving here by Back from
    // that record page is the opposite: go back to the list.
    if (action === "advance") {
      if (returningToHandoff.current) {
        navigate(currentStep.route);
        return;
      }
      next();
      return;
    }
    // Issue #2688: a RECORD-SCOPED tour (e.g. the deal-file or File Cover Page
    // tour) can only ever be MEANT to run on a concrete matching record -- there
    // is no real id to substitute when we are not already on one. If the step
    // route still carries a ``:param`` segment here, navigating to the LITERAL
    // pattern string (e.g. ``/crm/deals/:id``) would render that literal
    // segment, ``useParams()`` would read the text ``":id"``, the page would
    // 404, and every remaining step would then stall for the full 8s
    // ``targetWaitTimeout`` waiting for an anchor that never mounts. End the
    // tour gracefully instead of navigating nowhere useful. The entry points
    // (GuidedToursPanel's section list, TourLauncher's route gate) are
    // themselves guarded so this branch should not normally be reached, but it
    // is the last line of defense for any other launch path (e.g. a ``?tour=``
    // deep-link) that bypasses those gates.
    if (action === "cancel") {
      finish("cancelled");
      return;
    }
    // A static (``:``-free) route keeps exact prior behaviour.
    navigate(currentStep.route);
  }, [run, currentStep, activeTourId, location.pathname, navigate, finish, next]);

  // Modal-open driver for action/modal steps (issue #1342). A step may carry
  // ``openVia`` -- the data-tour anchor of a pure, side-effect-free UI toggle
  // that OPENS the modal this step points into. When the engine arrives at such
  // a step on the right route and the step's own target is NOT yet mounted, we
  // dispatch a single click on the opener so the modal mounts; react-joyride's
  // ``targetWaitTimeout`` (8s, set on <Joyride>) then waits for the anchor and
  // highlights it -- this is what stops the lazy/modal-anchor race from firing
  // TARGET_NOT_FOUND and ending the tour early. We click at most ONCE per step
  // arrival (guarded by openedForStep) and never when the modal is already open
  // (its target is already present), so re-renders cannot re-toggle it. The
  // SAFETY CONTRACT (see TourStep.openVia) is that the opener only opens a modal
  // -- it never submits or mutates -- so a walkthrough never changes data.
  useEffect(() => {
    if (!run || !currentStep || !onRoute) return;
    const openVia = currentStep.openVia;
    if (!openVia) {
      openedForStep.current = null;
      return;
    }
    // Already handled this exact step? (refs survive the re-renders the click
    // triggers, so we never click twice for one arrival.)
    if (openedForStep.current === currentStep.id) return;
    // If the modal's own target is already mounted, the modal is open -- nothing
    // to do (e.g. the user stepped Back into it).
    if (
      currentStep.target !== "center" &&
      document.querySelector(anchorSelector(currentStep.target))
    ) {
      openedForStep.current = currentStep.id;
      return;
    }
    const stepId = currentStep.id;
    const clickOpener = (): boolean => {
      const opener = document.querySelector<HTMLElement>(
        `[data-tour="${openVia}"]`,
      );
      if (!opener) return false;
      openedForStep.current = stepId;
      modalStepOpen.current = stepId;
      opener.click();
      return true;
    };
    if (clickOpener()) return;
    // The opener is not mounted yet -- typically a page still loading its
    // record after the tour arrived on it (the D9 WP24 hand-off lands on a deal
    // page mid-fetch, #3551). Nothing re-runs this effect when the page
    // finishes rendering, so poll for the opener for as long as react-joyride
    // waits for the target (``targetWaitTimeout``) and click it once it
    // appears. Still at most ONE click per arrival.
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (clickOpener() || Date.now() - started > OPENER_WAIT_MS) {
        window.clearInterval(timer);
      }
    }, OPENER_POLL_MS);
    return () => window.clearInterval(timer);
  }, [run, currentStep, onRoute]);

  // Layout-shift watch (#3624, D4): re-measure when the target moves.
  useEffect(() => {
    if (!run || !currentStep || !onRoute || currentStep.target === "center") return;
    const target = currentStep.target;
    let last = "";
    const timer = window.setInterval(() => {
      const el = findHintTarget(target);
      if (!el) return;
      const key = layoutKey(el);
      if (last && key !== last) window.dispatchEvent(new Event("resize"));
      last = key;
    }, LAYOUT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [run, currentStep, onRoute]);

  // Tour-opened-modal CLEANUP (issue #1342). A step driven open via ``openVia``
  // records its id in ``modalStepOpen``. This effect closes that modal the moment
  // the tour LEAVES that step -- whether by Next/Back, ending the tour, or the
  // tour being torn down -- so a walkthrough never strands an open dialog. We do
  // it on the step-change transition (and on unmount) rather than inside the
  // end-of-tour event, because react-joyride ends the run through its own state
  // and the finish event path proved unreliable while a focus-trapping modal was
  // mounted. We close by clicking the shared Modal's close (X) button (its
  // ``onClose``); Escape is the fallback. Safe: the opener only ever OPENS a
  // modal, so closing it discards the un-submitted form with no side effect.
  const activeStepId = run ? (currentStep?.id ?? null) : null;
  useEffect(() => {
    const opened = modalStepOpen.current;
    if (!opened) return;
    if (opened === activeStepId) return; // still on the modal step -- keep it
    modalStepOpen.current = null;
    const dialog = document.querySelector('[role="dialog"]');
    if (!dialog) return;
    const closeBtn = dialog.querySelector<HTMLElement>(
      'button[aria-label="Close"]',
    );
    if (closeBtn) {
      closeBtn.click();
    } else {
      document.dispatchEvent(
        new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
      );
    }
  }, [activeStepId]);

  // Deep-link autostart (?tour=<id>) — used for demos + visual verification.
  // Waits until the tour is actually available (permissions loaded) before
  // firing, and only ever fires once.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (autoStartedRef.current) return;
    // Wait until auth + modules have resolved so step gating is computed against
    // the user's FINAL permissions (otherwise a flag-gated step could be wrongly
    // filtered while the user is still loading).
    if (authLoading || modulesLoading) return;
    const tourId = new URLSearchParams(location.search).get("tour");
    if (!tourId) return;
    const tour = getTour(tourId);
    if (!tour) {
      autoStartedRef.current = true;
      return;
    }
    if (!isTourAvailable(tour, gate)) return; // perms not ready yet — retry
    autoStartedRef.current = true;
    startTour(tourId);
  }, [location.search, gate, startTour, authLoading, modulesLoading]);

  const handleEvent = useCallback(
    (data: EventData) => {
      if (!runRef.current) return;
      const { action, status, type } = data;
      if (status === STATUS.SKIPPED) {
        finish("skipped");
        return;
      }
      if (status === STATUS.FINISHED) {
        finish("completed");
        return;
      }
      // X / Esc / overlay click — cancel cleanly (REQ D4-ONB-003).
      if (action === ACTIONS.CLOSE) {
        finish("cancelled");
        return;
      }
      // Never stall on a genuinely-missing target -- advance past it. But ONLY
      // when we are already ON the step's route: off-route, the target is absent
      // simply because the cross-route navigation is still in flight (joyride is
      // paused via run && onRoute), and auto-advancing there silently SKIPS the
      // step -- which was dropping the lazy wizard-route action steps (#1342).
      // On-route, react-joyride's targetWaitTimeout (8s) has already elapsed, so
      // the target really is missing and advancing is correct.
      if (type === EVENTS.TARGET_NOT_FOUND && onRouteRef.current) {
        next();
      }
    },
    [finish, next],
  );

  const agency = branding.agency_name;
  // page=spotlight / action=beacon convention (issue #1341) — applied in the
  // shared, unit-tested ``toJoyrideStep`` helper so every tour/profile inherits
  // it engine-side with no per-tour edit.
  // A hint targets the copy of its control that is on screen (a phone and a
  // desktop layout can both render it), not the first one in the document.
  const hinting = isHintTourId(activeTourId);
  const joyrideSteps: Step[] = useMemo(
    () =>
      steps.map((s) => {
        const step = toJoyrideStep(s, (copy) => interpolateAgency(copy, agency));
        return hinting ? { ...step, target: () => findHintTarget(s.target) } : step;
      }),
    [steps, agency, hinting],
  );

  // Below-the-fold scroll offset (issue #1981): react-joyride scrolls a target
  // to the top of the scrolling ``<main>``, where the ``sticky top-0`` header
  // (the FIRST CHILD of that scroll parent, per #1739) would occlude the
  // spotlight. We push the target down by the LIVE header height + breathing
  // room so it clears the bar. Re-read per route/step because the header height
  // differs between the admin and CRM shells and across breakpoints.
  const scrollOffset = useMemo(() => {
    // Reference the route + step so the offset RE-DERIVES when the shell (route)
    // or the active step changes -- the header element is the same but its
    // measured height varies by shell (admin vs crm) and breakpoint, so a stale
    // memo would scroll a later step by the wrong amount.
    void location.pathname;
    void stepIndex;
    return stickyHeaderOffset();
  }, [location.pathname, stepIndex]);

  const value: TourContextValue = useMemo(
    () => ({
      activeTourId,
      isRunning: run,
      currentStep,
      isHint: run && isHintTourId(activeTourId),
      stepIndex,
      stepCount: steps.length,
      hasNextSection: nextSectionIndex(steps, stepIndex) !== -1,
      startTour,
      next,
      back,
      skipSection,
      endTour,
      showHint,
      navGroupToExpand:
        run && currentStep ? (currentStep.navGroup ?? null) : null,
      mobileDrawerOpen: Boolean(run && currentStep?.navGroup),
    }),
    [
      activeTourId,
      run,
      currentStep,
      stepIndex,
      steps,
      startTour,
      next,
      back,
      skipSection,
      endTour,
      showHint,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {steps.length > 0 && (
        <Joyride
          steps={joyrideSteps}
          run={run && onRoute}
          stepIndex={stepIndex}
          continuous
          tooltipComponent={TourTooltip}
          onEvent={handleEvent}
          // Engine-level "tooltip always fully on-screen" guarantee (issue
          // #1357): every step's mapper already carries these floatingOptions;
          // passing the same default at the props level is belt-and-braces (the
          // two compose via react-joyride's deepMerge) so a future step that
          // sets its own floatingOptions still inherits the viewport clamp.
          floatingOptions={TOUR_FLOATING_OPTIONS}
          options={{
            targetWaitTimeout: 8000,
            overlayClickAction: "close",
            dismissKeyAction: "close",
            zIndex: 10000,
            // Below-the-fold scroll clears the sticky header (issue #1981).
            scrollOffset,
            scrollDuration: 400,
          }}
        />
      )}
    </TourContext.Provider>
  );
}
